/**
 * The core routes this surface is allowed to call.
 *
 * Every path below is registered in `services/core/routes.go`. Three of them
 * are public reads; the rest are commands that core admits either
 * unauthenticated with a guest identity, or with the `lp_customer_session`
 * HttpOnly cookie plus the CSRF header (`optionalCustomerMutation`).
 *
 * There is no PocketBase collection path here and no admin path. Adding either
 * would violate ADR-003 and MASTER_SPEC 14.2: the browser holds no bearer token
 * and performs no generic collection CRUD.
 */

export const CATALOG_PATH = "/api/core/catalog" as const;
export const CUSTOMER_SESSION_PATH = "/api/core/customer/sessions" as const;
export const CHECKOUT_QUOTES_PATH = "/api/core/checkout/quotes" as const;
export const INQUIRIES_PATH = "/api/core/inquiries" as const;

export const CSRF_HEADER = "X-CSRF-Token" as const;
export const CORRELATION_HEADER = "X-Correlation-ID" as const;
/** Guest continuation capability for a checkout with no customer session. */
export const CHECKOUT_TOKEN_HEADER = "X-Checkout-Token" as const;

export function productPath(productID: string): string {
  return `/api/core/catalog/products/${encodeURIComponent(productID)}`;
}

export function itemPath(itemID: string): string {
  return `/api/core/catalog/items/${encodeURIComponent(itemID)}`;
}

export function checkoutStatusPath(quoteID: string): string {
  return `${CHECKOUT_QUOTES_PATH}/${encodeURIComponent(quoteID)}`;
}

export function checkoutContinuePath(orderID: string): string {
  return `/api/core/checkout/orders/${encodeURIComponent(orderID)}/continue`;
}

export interface CatalogQuery {
  readonly search: string;
  readonly platform: string;
  readonly region: string;
  readonly limit: number;
  readonly offset: number;
}

/** Core caps `limit` at 50 and `offset` at 500; the page size is fixed here. */
export const CATALOG_PAGE_SIZE = 24;
export const CATALOG_MAX_OFFSET = 500;

export function catalogPath(query: CatalogQuery): string {
  const search = new URLSearchParams();
  if (query.search.length > 0) search.set("q", query.search);
  if (query.platform.length > 0) search.set("platform", query.platform);
  if (query.region.length > 0) search.set("region", query.region);
  search.set("limit", String(query.limit));
  search.set("offset", String(query.offset));
  return `${CATALOG_PATH}?${search.toString()}`;
}
